import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Select, Table, Typography } from "@coolremote/shared";
import styles from "./PropertyMappingDialog.module.css";

interface Props {
  open: boolean;
  paramName?: string;
  propertyName?: string;
  /** Universal property enum entries — one row per entry (left column label). */
  universalEntries: { value: string; label: string }[];
  /** Register's customEnum — options shown in each row's right-side dropdown. */
  registerEnum: Record<string, string>;
  /** Current mapping: { [universalEnumValue]: registerEnumKey } */
  value?: Record<string, string>;
  onClose: () => void;
  onSave: (mapping: Record<string, string>) => void;
  readOnly?: boolean;
}

const PropertyMappingDialog: React.FC<Props> = ({
  open,
  paramName,
  propertyName,
  universalEntries,
  registerEnum,
  value,
  onClose,
  onSave,
  readOnly,
}) => {
  const [mapping, setMapping] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) setMapping(value || {});
  }, [open, value]);

  // Build the register options list; disable values already mapped to another universal key.
  const usedRegisterKeys = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping],
  );

  const registerOptions = useMemo(
    () => [
      { value: "", label: t`— None —` },
      ...Object.entries(registerEnum).map(([key, label]) => ({
        value: key,
        label,
        disabled: usedRegisterKeys.has(key),
      })),
    ],
    [registerEnum, usedRegisterKeys],
  );

  const handleChange = (universalValue: string, registerKey: string) =>
    setMapping((prev) => {
      const next = { ...prev };
      if (registerKey) next[universalValue] = registerKey;
      else delete next[universalValue];
      return next;
    });

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={paramName ? `${t`Property Mapping`} — ${paramName}` : t`Property Mapping`}
      onConfirm={readOnly ? undefined : () => onSave(mapping)}
      hideConfirm={readOnly}
      confirmLabel={t`Save`}
      cancelLabel={readOnly ? t`Close` : t`Cancel`}
      confirmSize="small"
      cancelSize="small"
      size="small"
    >
      <div className={styles.content}>
        {propertyName && (
          <Typography component="p" className={styles.hint}>
            {t`Map`} <strong>{propertyName}</strong> {t`values to register values`}
          </Typography>
        )}
        {universalEntries.length === 0 ? (
          <Typography component="p" className={styles.hint}>
            {t`No enum values found for this universal property.`}
          </Typography>
        ) : Object.keys(registerEnum).length === 0 ? (
          <Typography component="p" className={styles.hint}>
            {t`No register enum values defined. Set the register Map values first.`}
          </Typography>
        ) : (
          <Table
            data={universalEntries}
            getRowKey={(row) => row.value}
            columns={[
              {
                id: "universal",
                title: t`Universal Value`,
                width: "45%",
                render: (row: { value: string; label: string }) => row.label,
              },
              {
                id: "register",
                title: t`Register Value`,
                render: (row: { value: string; label: string }) => (
                  <Select
                    fullWidth
                    menuPortal
                    options={registerOptions.map((opt) => ({
                      ...opt,
                      disabled: opt.value !== "" && opt.value !== mapping[row.value] && usedRegisterKeys.has(opt.value),
                    }))}
                    value={mapping[row.value] || ""}
                    disabled={readOnly}
                    onChange={(v: string | number) => handleChange(row.value, String(v))}
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </BaseDialog>
  );
};

export default PropertyMappingDialog;
